import React, { useMemo } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number } from "../../../config";
import { EXPRESSION_LABELS } from "../ExpressionBuilder/constants/expressionBuilder.constants";
import "../ExpressionBuilder/styles/ExpressionBuilder.css";
import { useWorkflowContext } from "../WorkflowContextProvider";
import MultiDimensionColumnPopup from "./MultiDimensionColumnPopup";
import MultiDimensionColumnFilterPopup from "./columnFilter/MultiDimensionColumnFilterPopup";
import { useVariablesContext } from "./context/variableContext";
import { useMultiDimensionChipPropertiesModal } from "./hooks/useMultiDimensionChipPropertiesModal";
import "./workflowVariable.scss";
import {
  MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID,
  WORKFLOW_VARIABLE_LABELS,
  getMultiDimensionOptionsColumnIcon,
  normalizeMultiDimensionType,
} from "./workflowVariables.constants";

/**
 * Full-screen overlay for multi-dimension chip configuration (workflow designer & form builder).
 */
const MultiDimensionChipPropertiesModal = () => {
  const {
    isOpen,
    dimensions,
    options,
    displayedOptions,
    columnFilters,
    dimensionFilterPopup,
    dimensionFilterPopupAnchorRef,
    lastAddedRowId,
    columnPopup,
    popupName,
    popupType,
    nameMax,
    atMaxColumns,
    saveDisabledAdd,
    saveDisabledUpdate,
    isAddingOptionRow,
    handleClose,
    handleAddRow,
    showAddRowClearsFiltersDialog,
    dismissAddRowClearFiltersDialog,
    confirmAddRowAfterClearFilters,
    openAddColumnPopup,
    openEditColumnPopup,
    openColumnFilterForDimension,
    optionsColumnFilterDimension,
    applyColumnFilter,
    clearColumnFilter,
    dismissColumnFilterPopup,
    dismissColumnPopup,
    handleColumnPopupCancel,
    handlePopupNameChange,
    handlePopupTypeChange,
    handlePopupSaveAdd,
    handlePopupUpdateColumn,
    handlePopupDeleteColumn,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleOptionLabelChange,
    handleCellChange,
    handleDeleteOptionRow,
    handleDeleteAllOptionRows,
    handleOptionPersistBlur,
    handleOptionRowBlur,
    getDimensionIcon,
    columnPopupAnchorRef,
  } = useMultiDimensionChipPropertiesModal();
  const { selectedVariable } = useWorkflowContext();
  const { variableName } = useVariablesContext();

  const multiDimensionModalTitle = useMemo(() => {
    const fromContext = (variableName || "").trim();
    return `${fromContext} `;
  }, [variableName]);

  if (!isOpen) {
    return null;
  }

  const filterPopupDimension =
    dimensionFilterPopup &&
    (String(dimensionFilterPopup.dimensionId) === String(MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID)
      ? optionsColumnFilterDimension
      : dimensions?.find((d) => String(d?.id) === String(dimensionFilterPopup?.dimensionId)));

  const modalBody = (
    <div
      className='expression-builder-modal-content multi-dimension-dialog-inner d-flex flex-column align-items-stretch h-100'
      style={{ minHeight: 0 }}>
      <div className='expression-builder-modal-header d-flex align-items-center  pb-1 justify-content-between'>
        <div className='d-flex align-items-center gap-2'>
          <h5 className='mb-0 text-body font-weight-semibold'>{WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION}</h5>
        </div>
        <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
          <Button
            onClick={handleClose}
            icon='close'
            fillMode='flat'
            className='btn-close ms-auto'
            id='close-multi-dimension-modal'
            title={EXPRESSION_LABELS.CLOSE}
          />
        </Tooltip>
      </div>

      <div className='expression-builder-modal-body position-relative multi-dimension-modal-body'>
        <div className='multi-dimension-modal-table-scroll multi-dimension-modal-table-scroll--with-float-btn'>
          <div className='table-responsive'>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              <table className='table table-bordered mb-0'>
                <thead className='bg-light'>
                  <tr>
                    <th className='multi-dimension-modal-table-cell' aria-label='Drag' />
                    <th className='font-weight-normal'>
                      <div className='d-flex align-items-center justify-content-between'>
                        <div className='d-flex align-items-center gap-1 pointer-events-none '>
                          <span className='d-inline-flex align-items-center link-text'>
                            {getMultiDimensionOptionsColumnIcon(!!selectedVariable?.attributes?.numberOnly)}
                          </span>
                          <span className='link-text '>{multiDimensionModalTitle}</span>
                        </div>
                        <button
                          type='button'
                          className={`btn btn-link p-0 multi-dimension-column-filter-btn flex-shrink-0 ${
                            columnFilters?.[MULTI_DIMENSION_OPTIONS_COLUMN_FILTER_ID] ? "multi-dimension-column-filter-btn--active" : ""
                          }`}
                          onClick={(e) => openColumnFilterForDimension(optionsColumnFilterDimension, e)}
                          title={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_TITLE}
                          aria-label={`${WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_TITLE}: ${WORKFLOW_VARIABLE_LABELS.OPTIONS_COLUMN_LABEL}`}>
                          <span className='opacity-75' aria-hidden>
                            {icon.FILTER}
                          </span>
                        </button>
                      </div>
                    </th>
                    {dimensions.map((dim) => (
                      <th key={dim.id} className='font-weight-normal'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-left multi-dimension-column-header-btn d-flex align-items-center gap-1 text-body link-text '
                            onClick={(e) => openEditColumnPopup(dim, e)}>
                            <span className='d-inline-flex align-items-center'>{getDimensionIcon(dim.type)}</span>
                            <div className='multi-dimension-option-actions-name'>{dim.name}</div>
                          </button>
                          <button
                            type='button'
                            className={`btn btn-link p-0 multi-dimension-column-filter-btn flex-shrink-0 ${
                              columnFilters?.[dim.id] ? "multi-dimension-column-filter-btn--active" : ""
                            }`}
                            onClick={(e) => openColumnFilterForDimension(dim, e)}
                            title={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_TITLE}
                            aria-label={`${WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_FILTER_TITLE}: ${dim.name}`}>
                            <span className='opacity-75' aria-hidden>
                              {icon.FILTER}
                            </span>
                          </button>
                        </div>
                      </th>
                    ))}
                    <th className='multi-dimension-option-actions-header p-1' aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {displayedOptions.map((opt) => (
                    <tr key={opt.id} className='multi-dimension-option-row' onBlur={(e) => handleOptionRowBlur(opt.id, e)}>
                      <td className='align-middle p-1'>
                        <div className='d-inline-flex align-items-center multi-dimension-option-row-actions'>
                          <span
                            className='drag-icon cursor-move d-inline-flex'
                            draggable
                            onDragStart={() => handleDragStart(opt.id)}
                            onDragOver={(e) => handleDragOver(e, opt.id)}
                            onDragEnd={handleDragEnd}
                            aria-label='Drag row'>
                            {icon.DRAG_INDICATOR}
                          </span>
                        </div>
                      </td>
                      <td className='align-middle'>
                        <Input
                          autoComplete='off'
                          type={selectedVariable?.attributes?.numberOnly ? "number" : "text"}
                          autoFocus={String(opt.id) === String(lastAddedRowId)}
                          className='form-control form-control-sm border-0 bg-transparent'
                          value={opt.label || ""}
                          onChange={(e) => handleOptionLabelChange(opt.id, e.target.value)}
                        />
                      </td>
                      {dimensions.map((dim) => {
                        const dimType = normalizeMultiDimensionType(dim.type);
                        const cellValue = opt.cells?.[dim.id] ?? "";
                        return (
                          <td key={dim.id} className={"align-middle p-1"}>
                            {dimType === label.TEXT ? (
                              <TextArea
                                autoComplete='off'
                                autoSize
                                maxLength={number.FIVE_HUNDRED}
                                rows={number.ONE}
                                resizable='none'
                                className='form-control p-0  multi-dimension-cell-textarea '
                                value={cellValue}
                                onChange={(e) => handleCellChange(opt.id, dim.id, e.target.value)}
                              />
                            ) : (
                              <Input
                                autoComplete='off'
                                type={dim.type}
                                className='form-control '
                                value={cellValue}
                                onChange={(e) => handleCellChange(opt.id, dim.id, e.target.value)}
                              />
                            )}
                          </td>
                        );
                      })}
                      <td className='align-middle p-1 multi-dimension-option-actions-cell text-right'>
                        {options.length > number.ONE && (
                          <button
                            title='Delete row'
                            type='button'
                            className='btn btn-link p-0 text-danger d-inline-flex align-items-center multi-dimension-option-delete-btn'
                            onClick={() => handleDeleteOptionRow(opt.id)}>
                            {icon.EMAIL_TRASH}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Tooltip>
          </div>
        </div>

        {filterPopupDimension && dimensionFilterPopup && (
          <MultiDimensionColumnFilterPopup
            key={dimensionFilterPopup.dimensionId}
            dimension={filterPopupDimension}
            anchorRef={dimensionFilterPopupAnchorRef}
            savedComposite={columnFilters[dimensionFilterPopup.dimensionId]}
            onApply={(composite) => applyColumnFilter(dimensionFilterPopup.dimensionId, composite)}
            onClear={() => clearColumnFilter(dimensionFilterPopup.dimensionId)}
            onClose={dismissColumnFilterPopup}
          />
        )}

        {columnPopup && (
          <MultiDimensionColumnPopup
            anchorRef={columnPopupAnchorRef}
            mode={columnPopup.mode}
            popupName={popupName}
            nameMax={nameMax}
            columnType={popupType}
            saveDisabledAdd={saveDisabledAdd}
            saveDisabledUpdate={saveDisabledUpdate}
            onClose={dismissColumnPopup}
            onNameChange={handlePopupNameChange}
            onTypeChange={handlePopupTypeChange}
            onCancel={handleColumnPopupCancel}
            onSaveAdd={handlePopupSaveAdd}
            onUpdateColumn={handlePopupUpdateColumn}
            onDeleteColumn={handlePopupDeleteColumn}
          />
        )}
        <div className='multi-dimension-add-row-float text-left-button'>
          <button className='btn btn-outline-danger btn-sm button-add mr-2 delete-all-btn shadow-sm px-3 py-2' onClick={handleDeleteAllOptionRows} type='button'>
            {WORKFLOW_VARIABLE_LABELS.DELETE_ALL}
          </button>
        </div>

        <div className='multi-dimension-add-row-float text-right-button'>
          <button
            className='btn btn-tertiary btn-sm button-add shadow-sm px-3 py-2  mr-2'
            onClick={(e) => openAddColumnPopup(e)}
            disabled={atMaxColumns}
            title={atMaxColumns ? WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_MAX_COLUMNS_REACHED : undefined}>
            {WORKFLOW_VARIABLE_LABELS.ADD_COLUMN_LABEL}
          </button>
          <button
            className='btn btn-tertiary btn-sm button-add shadow-sm px-3 py-2'
            onClick={handleAddRow}
            disabled={isAddingOptionRow}
            type='button'>
            {WORKFLOW_VARIABLE_LABELS.ADD_ROW_LABEL}
          </button>
        </div>
      </div>
    </div>
  );

  const addRowFiltersDialog =
    showAddRowClearsFiltersDialog && typeof document !== "undefined" ? (
      <Dialog
        onClose={dismissAddRowClearFiltersDialog}
        title={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_ADD_ROW_CLEARS_FILTERS_TITLE}
        width={420}
        className='multi-dimension-add-row-clear-filters-dialog'>
        <p className='px-1 mb-0'>{WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_ADD_ROW_CLEARS_FILTERS_MESSAGE}</p>
        <DialogActionsBar>
          <div className='d-flex justify-content-end gap-2 w-100 px-3 py-2'>
            <Button fillMode='outline' onClick={dismissAddRowClearFiltersDialog}>
              {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_ADD_ROW_CLEARS_FILTERS_CANCEL}
            </Button>
            <Button themeColor='primary' onClick={confirmAddRowAfterClearFilters}>
              <span className='d-inline-flex align-items-center gap-1'>
                {icon.CHECK_CIRCLE}
                {WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION_ADD_ROW_CLEARS_FILTERS_CONFIRM}
              </span>
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    ) : null;

  return (
    <>
      <Dialog show={isOpen} onClose={handleClose} width='100%' height='100%' className='form-builder-dialog'>
        {modalBody}
      </Dialog>
      {addRowFiltersDialog}
    </>
  );
};

export default MultiDimensionChipPropertiesModal;
